'use client';

import { Header } from "@/components/landing/header";
import { Footer } from "@/components/landing/footer";
import { useState, useEffect, useRef, Suspense, ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ReCAPTCHA from 'react-google-recaptcha';
import { openingsData } from '@/lib/jobs-data';
import { serviceOptions } from '@/lib/services-form-data';
import { contactInfo, socialLinks } from '@/lib/site-config';
import { getFirestore, addDoc, collection } from "firebase/firestore";
import { initializeFirebase } from "@/firebase";
import { useToast } from "@/hooks/use-toast";
import { errorEmitter } from "@/firebase/error-emitter";
import { FirestorePermissionError } from "@/firebase/errors";
import { Layers, ChevronDown, User, Mail, DollarSign, Calendar, Briefcase, Link as LinkIcon, Send, Loader2, MapPin, Phone, Building } from "lucide-react";


// Section 1: Hero
function ContactHero({ title, description, bannerText }: { title: ReactNode; description: string; bannerText: string }) {
  return (
    <section className="relative w-full min-h-[70vh] flex items-center justify-center pt-28 pb-12 overflow-hidden liquid-bg">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
        <div className="animate-fade-in-up flex flex-col items-center justify-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel-intense border border-white/20 text-white/90 text-sm font-medium mb-4">
            <span className="w-2 h-2 rounded-full bg-neon-blue animate-pulse"></span>
            {bannerText}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight drop-shadow-2xl text-white">
            {title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed font-light tracking-wide">
            {description}
          </p>
        </div>
      </div>
    </section>
  );
}

// Section 2: Form
const formSchema = z.object({
  inquiryType: z.enum(["quotation", "job", "general"]),
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  mobile: z.string().regex(/^[\d\s()+-]*$/, { message: "Please enter a valid phone number" }).optional().or(z.literal('')),
  message: z.string().min(10, "Message must be at least 10 characters"),
  service: z.string().optional(),
  budget: z.string().optional(),
  deliveryTime: z.string().optional(),
  jobTitle: z.string().optional(),
  linkedin: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.inquiryType === 'quotation') {
    if (!data.service || data.service.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['service'],
        message: 'Please select a service of interest',
      });
    }
    if (!data.deliveryTime || data.deliveryTime.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['deliveryTime'],
        message: 'Expected delivery time is required',
      });
    }
  }
  if (data.inquiryType === 'job') {
    if (!data.jobTitle || data.jobTitle.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['jobTitle'],
        message: 'Please select the job you are applying for',
      });
    }
    if (!data.linkedin || data.linkedin.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['linkedin'],
        message: 'LinkedIn or portfolio URL is required',
      });
    } else {
        const urlCheck = z.string().url("A valid LinkedIn or portfolio URL is required").safeParse(data.linkedin);
        if(!urlCheck.success) {
             ctx.addIssue({
                code: 'custom',
                path: ['linkedin'],
                message: 'A valid LinkedIn or portfolio URL is required',
            });
        }
    }
  }
});


type FormData = z.infer<typeof formSchema>;

const jobTitles = [...openingsData.map(job => job.title), 'Other'];

function XIcon() {
    return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 7.184L18.901 1.153zm-1.653 19.47h2.61l-10.59-12.088H7.058l10.192 12.088z"/></svg>
    )
}

function FacebookIcon() {
  return (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>
  )
}

function YoutubeIcon() {
  return (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
  )
}
  
function LinkedInIcon() {
    return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path></svg>
    )
}

function FormSection() {
    const searchParams = useSearchParams();
    const initialInquiry = searchParams.get("inquiry");
    const initialTitle = searchParams.get("title");
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { register, handleSubmit, formState: { errors, isValid }, setValue, watch, reset } = useForm<FormData>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            inquiryType: "quotation",
            name: "",
            email: "",
            mobile: "",
            message: "",
            service: "",
            budget: "",
            deliveryTime: "",
            jobTitle: "",
            linkedin: "",
        },
    });

    const inquiryType = watch("inquiryType");

    useEffect(() => {
        const inquiry = (initialInquiry === 'job' || initialInquiry === 'quotation' || initialInquiry === 'general') ? initialInquiry : 'quotation';
        setValue('inquiryType', inquiry, { shouldValidate: true });

        if (inquiry === 'job' && initialTitle) {
            setValue('jobTitle', initialTitle, { shouldValidate: true });
        }
    }, [initialInquiry, initialTitle, setValue]);

    const onSubmit = (data: FormData) => {
        if (!captchaToken) {
            toast({
                variant: "destructive",
                title: "CAPTCHA Required",
                description: "Please complete the CAPTCHA before submitting.",
            });
            return;
        }
        setIsSubmitting(true);
        
        const app = initializeFirebase();
        const db = getFirestore(app);
        const submissionData = {
            ...data,
            submittedAt: new Date(),
        };
        const contactsCollection = collection(db, "contacts");

        addDoc(contactsCollection, submissionData)
            .then(() => {
                toast({
                    title: "Message Sent!",
                    description: "Thank you for reaching out. We'll get back to you shortly.",
                });
                reset();
                recaptchaRef.current?.reset();
                setCaptchaToken(null);
            })
            .catch(async (serverError) => {
                const permissionError = new FirestorePermissionError({
                    path: contactsCollection.path,
                    operation: 'create',
                    requestResourceData: submissionData,
                });
                errorEmitter.emit('permission-error', permissionError);
                toast({
                    variant: "destructive",
                    title: "Submission Error",
                    description: "Could not send your message. Please try again later.",
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <section className="py-24 bg-background relative overflow-hidden" id="contact-form">
            <div className="absolute top-0 left-0 w-full h-full bg-grid-slate-200/50 dark:bg-grid-slate-800/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
                    <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                        <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">Send us a Message</h2>
                        <p className="text-slate-500 dark:text-slate-400 mb-8">Fill out the form below and our team will get back to you within 24 hours.</p>
                        <form onSubmit={handleSubmit(onSubmit)} className="glass-panel-intense p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-2xl relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            
                            <div className="space-y-2 mb-6">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">I'm interested in... <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Layers className="text-slate-400 text-lg" />
                                    </div>
                                    <select {...register("inquiryType")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 text-slate-600 dark:text-slate-300 appearance-none">
                                        <option value="quotation">Getting a Free Quotation</option>
                                        <option value="job">Applying for a Job</option>
                                        <option value="general">A General Inquiry</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <ChevronDown className="text-slate-400" />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Your Name <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="text-slate-400 text-lg" />
                                        </div>
                                        <input {...register("name")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white" placeholder="John Doe" type="text" />
                                    </div>
                                    {errors.name && <p className="text-red-500 text-xs mt-1 ml-1">{errors.name.message}</p>}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Email Address <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="text-slate-400 text-lg" />
                                        </div>
                                        <input {...register("email")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white" placeholder="john@example.com" type="email" />
                                    </div>
                                    {errors.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email.message}</p>}
                                </div>
                            </div>

                            <div className="space-y-2 mb-6">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Mobile Number (Optional)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Phone className="text-slate-400 text-lg" />
                                    </div>
                                    <input {...register("mobile")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white" placeholder="e.g. +91 98765 43210" type="tel" />
                                </div>
                                {errors.mobile && <p className="text-red-500 text-xs mt-1 ml-1">{errors.mobile.message}</p>}
                            </div>

                            {inquiryType === 'quotation' && (
                                <>
                                    <div className="space-y-2 mb-6">
                                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Service of Interest <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Layers className="text-slate-400 text-lg" />
                                            </div>
                                            <select {...register("service")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 text-slate-600 dark:text-slate-300 appearance-none">
                                                {serviceOptions.map(s => <option key={s}>{s}</option>)}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <ChevronDown className="text-slate-400" />
                                            </div>
                                        </div>
                                        {errors.service && <p className="text-red-500 text-xs mt-1 ml-1">{errors.service.message}</p>}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Your Budget (Optional)</label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <DollarSign className="text-slate-400 text-lg" />
                                                </div>
                                                <input {...register("budget")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white" placeholder="e.g., $5,000" type="text" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Expected Delivery <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Calendar className="text-slate-400 text-lg" />
                                                </div>
                                                <input {...register("deliveryTime")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white" placeholder="e.g., 3-6 Months" type="text" />
                                            </div>
                                            {errors.deliveryTime && <p className="text-red-500 text-xs mt-1 ml-1">{errors.deliveryTime.message}</p>}
                                        </div>
                                    </div>
                                </>
                            )}

                            {inquiryType === 'job' && (
                                <>
                                    <div className="space-y-2 mb-6">
                                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Applying for <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Briefcase className="text-slate-400 text-lg" />
                                            </div>
                                            <select {...register("jobTitle")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 text-slate-600 dark:text-slate-300 appearance-none">
                                                {jobTitles.map(title => <option key={title} value={title}>{title}</option>)}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <ChevronDown className="text-slate-400" />
                                            </div>
                                        </div>
                                        {errors.jobTitle && <p className="text-red-500 text-xs mt-1 ml-1">{errors.jobTitle.message}</p>}
                                    </div>
                                    <div className="space-y-2 mb-6">
                                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">LinkedIn Profile or Portfolio URL <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <LinkIcon className="text-slate-400 text-lg" />
                                            </div>
                                            <input {...register("linkedin")} className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white" placeholder="https://linkedin.com/in/..." type="url" />
                                        </div>
                                        {errors.linkedin && <p className="text-red-500 text-xs mt-1 ml-1">{errors.linkedin.message}</p>}
                                    </div>
                                </>
                            )}
                            
                            <div className="space-y-2 mb-8">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Message <span className="text-red-500">*</span></label>
                                <textarea {...register("message")} className="w-full px-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-primary neon-input transition-all duration-300 placeholder-slate-400 dark:text-white resize-none" placeholder={inquiryType === 'quotation' ? "Tell us about your project..." : "Your message..."} rows={4}></textarea>
                                {errors.message && <p className="text-red-500 text-xs mt-1 ml-1">{errors.message.message}</p>}
                            </div>
                            <div className="mb-8 flex justify-center">
                                {siteKey ? (
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={siteKey}
                                        onChange={(token) => setCaptchaToken(token)}
                                        onExpired={() => setCaptchaToken(null)}
                                    />
                                ) : (
                                    <div className="p-4 bg-yellow-100 dark:bg-yellow-900/50 rounded-xl text-center text-sm text-yellow-800 dark:text-yellow-200">
                                        To enable form submission, please set the <code>NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY</code> environment variable.
                                    </div>
                                )}
                            </div>
                            <button type="submit" disabled={!siteKey || !captchaToken || !isValid || isSubmitting} className="w-full py-4 bg-gradient-to-r from-primary to-blue-600 text-white font-bold rounded-xl transform hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group/btn disabled:opacity-50 disabled:cursor-not-allowed">
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                  {isSubmitting ? 'Sending...' : 'Send Message'}
                                  {isSubmitting ? <Loader2 className="animate-spin" /> : <Send className="group-hover/btn:translate-x-1 transition-transform" />}
                                  </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-secondary to-green-500 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"></div>
                            </button>
                        </form>
                    </div>
                    <div className="space-y-10 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                        <div>
                            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-6">Contact Details</h2>
                            <div className="space-y-6">
                                <div className="group flex items-start gap-6 p-6 rounded-2xl border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-white/5 transition-all duration-300">
                                    <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                                        <MapPin className="text-3xl text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Corporate Office</h4>
                                        <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                                            {contactInfo.address}
                                        </p>
                                    </div>
                                </div>
                                <div className="group flex items-start gap-6 p-6 rounded-2xl border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-white/5 transition-all duration-300">
                                    <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                                        <Building className="text-3xl text-red-500" />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Registered Address</h4>
                                        <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                                            Sikri, Bulandshahr, UP, India. 203405.
                                        </p>
                                    </div>
                                </div>
                                <div className="group flex items-start gap-6 p-6 rounded-2xl border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-white/5 transition-all duration-300">
                                    <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                                        <Mail className="text-3xl text-secondary" />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Email Us</h4>
                                        <p className="text-slate-500 dark:text-slate-400 mb-1">{contactInfo.email}</p>
                                    </div>
                                </div>
                                <div className="group flex items-start gap-6 p-6 rounded-2xl border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-white/5 transition-all duration-300">
                                    <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                                        <Phone className="text-3xl text-accent" />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Call Us</h4>
                                        <p className="text-slate-500 dark:text-slate-400 mb-1">Mon-Fri from 8am to 5pm</p>
                                        <p className="text-xl font-semibold text-slate-700 dark:text-slate-200">{contactInfo.phone}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-6 pl-6">Follow Our Journey</h3>
                            <div className="flex gap-4 pl-6">
                                <a aria-label="Follow us on LinkedIn" className="w-12 h-12 rounded-full glass-panel-intense flex items-center justify-center hover:bg-[#0077b5] text-slate-400 hover:text-white transition-all duration-300 hover:animate-pop border border-slate-700/50" href={socialLinks.linkedin}>
                                    <LinkedInIcon />
                                </a>
                                <a aria-label="Follow us on X" className="w-12 h-12 rounded-full glass-panel-intense flex items-center justify-center hover:bg-black text-slate-400 hover:text-white transition-all duration-300 hover:animate-pop border border-slate-700/50" href={socialLinks.x}>
                                    <XIcon />
                                </a>
                                <a aria-label="Follow us on Facebook" className="w-12 h-12 rounded-full glass-panel-intense flex items-center justify-center hover:bg-[#1877F2] text-slate-400 hover:text-white transition-all duration-300 hover:animate-pop border border-slate-700/50" href={socialLinks.facebook}>
                                    <FacebookIcon />
                                </a>
                                <a aria-label="Follow us on YouTube" className="w-12 h-12 rounded-full glass-panel-intense flex items-center justify-center hover:bg-[#FF0000] text-slate-400 hover:text-white transition-all duration-300 hover:animate-pop border border-slate-700/50" href={socialLinks.youtube}>
                                    <YoutubeIcon />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Section 3: Map
function Map() {
    return (
        <section className="relative w-full h-[500px] bg-slate-900 border-y border-slate-800 overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
                <iframe
                    allowFullScreen={true}
                    className="map-dark-filter opacity-70 hover:opacity-90 transition-opacity duration-700"
                    height="100%"
                    loading="lazy"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d872.3316139535941!2d77.66807892828689!3d29.02638968697621!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390b67b8d40c6f4d%3A0xf039e3fa6432e304!2sDarshni%20Software!5e0!3m2!1sen!2sin!4v1722334057999!5m2!1sen!2sin"
                    style={{ border: 0, textShadow: '0 0 10px #000' }}
                    width="100%"
                ></iframe>
            </div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="relative">
                    <MapPin className="text-neon-blue text-6xl" style={{ textShadow: '0 0 10px #000' }} />
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-neon-blue rounded-full opacity-50 animate-ping"></div>
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-2 bg-black/50 blur-sm rounded-full"></div>
                </div>
            </div>
            <div className="absolute bottom-8 left-8 p-4 glass-panel-intense rounded-xl border border-white/10 hidden md:block">
                <p className="text-white text-sm font-bold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse"></span>
                    Open Now
                </p>
            </div>
        </section>
    );
}

function FormSectionLoading() {
  return (
    <div className="py-24 bg-background relative overflow-hidden" id="contact-form">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            <div className="animate-pulse">
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
                <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-8"></div>
                <div className="glass-panel-intense p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-2xl">
                    <div className="space-y-6">
                        <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                        </div>
                        <div className="h-24 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                        <div className="h-12 bg-slate-300 dark:bg-slate-600 rounded-xl"></div>
                    </div>
                </div>
            </div>
            <div className="animate-pulse space-y-10">
                <div>
                  <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-6"></div>
                  <div className="space-y-6">
                      <div className="h-24 bg-slate-200 dark:bg-slate-700 rounded-2xl"></div>
                      <div className="h-24 bg-slate-200 dark:bg-slate-700 rounded-2xl"></div>
                      <div className="h-24 bg-slate-200 dark:bg-slate-700 rounded-2xl"></div>
                  </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  )
}

function ContactPageContent() {
    const searchParams = useSearchParams();
    const inquiry = searchParams.get("inquiry");
    const source = searchParams.get("source");

    const defaultContent = {
        title: <>Let’s Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-neon-purple drop-shadow-sm">Something Great</span></>,
        description: "Have a project in mind? We turn ambitious ideas into digital reality. Reach out and let's start the conversation.",
        bannerText: "We'd love to hear from you"
    };

    const contentMap: { [key: string]: typeof defaultContent } = {
        job: {
            title: <>Join Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue drop-shadow-sm">Talented Team</span></>,
            description: "Ready to make an impact? We're looking for passionate individuals to help us build the future of software.",
            bannerText: "We're Hiring"
        },
        services: {
            title: <>Start Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue drop-shadow-sm">Next Project</span></>,
            description: "Tell us about your project requirements, and let's discuss how our services can help you achieve your goals.",
            bannerText: "From Concept to Code"
        },
        projects: {
            title: <>Inspired to <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-purple to-neon-blue drop-shadow-sm">Build Your Own?</span></>,
            description: "You've seen what we can do. Now, let's bring your vision to life with our expert development team.",
            bannerText: "Let's Create Together"
        },
        about: {
            title: <>Work With <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue drop-shadow-sm">Us</span></>,
            description: "Let's collaborate to build something amazing. Tell us about your project and let's get started.",
            bannerText: "Start a Partnership"
        },
        quotation: defaultContent,
        general: defaultContent
    };

    const effectiveKey = inquiry === 'job' ? 'job' : source || inquiry || 'general';
    const { title, description, bannerText } = contentMap[effectiveKey as keyof typeof contentMap] || defaultContent;
    
    return (
        <>
            <ContactHero title={title} description={description} bannerText={bannerText} />
            <FormSection />
            <Map />
        </>
    );
}

// Main Page Component
export default function ContactPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main>
        <Suspense fallback={<>
            <ContactHero 
                title={<>Get In <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-neon-purple drop-shadow-sm">Touch</span></>}
                description={"Loading tailored content for you..."}
                bannerText={"Connecting..."}
            />
            <FormSectionLoading />
            <Map />
        </>}>
          <ContactPageContent />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
